/**
 * Import Fuse.js
 *
 * @see https://fusejs.io/
 */
import Fuse from 'fuse.js';

// Import debounce
import debounce from 'debounce';

/**
 * External dependencies
 */
import { LitElement, html, css, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';

/**
 * Wordpress dependencies
 */
import apiFetch from '@wordpress/api-fetch';

// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __ } from '@wordpress/i18n';

import {
	uapRecipeTemplateLibraryConfig,
	uapRtlCreateFilterEvent
} from '../../utilities';

/**
 * Styles
 */
import styles from './web-component-style.scss?inline';

/**
 * Defines <uap-rtl-search> custom element
 */
@customElement( 'uap-rtl-search' )
class UAPRTL_Search extends LitElement {

	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties
	@property( { type: String, attribute:false } ) accessor query = '';

	@property( { type: Object, attribute: false } ) accessor filter = {};

	// Library config
	library = uapRecipeTemplateLibraryConfig;

	// Fuse search instance
	fuse = null;

	// Initialize a debounced search handler.
	_debouncedSearch = debounce(
		this.handleSearch.bind( this ), 400
	);

	/**
	 * Called when the component is updated
	 *
	 * @param {Map} changedProperties - The properties that changed
	 */
	updated( changedProperties ) {
		super.updated( changedProperties );
		// Check if the filter property has changed
		if ( changedProperties.has( 'filter' ) ) {
			// Check if the filter type is anything other than 'search'
			if ( this.filter.type !== 'search' ) {
				// Clear the search query
				this.query = '';
			}
		}
	}

	/**
	 * Initialize the search input
	 *
	 * @return {void}
	 */
	_initializeFuse() {
		if ( this.library && this.library.isReady && this.fuse === null ) {
			this.fuse = new Fuse( this.library.templates, {
				keys: this.library.searchKeys,
				minMatchCharLength: 3,
				threshold: 0.4,
				ignoreLocation: true,
				isCaseSensitive: false,
			} );
		}
	}

	/**
	 * Handle input event and update the query state
	 *
	 * @param {String} searchString - The input value
	 *
	 * @return {void}
	 */
	_handleInput( searchString ) {
		// No change in search string.
		if ( this.query === searchString ) {
			return;
		}
		// Call the debounced search handler
		this._debouncedSearch( searchString );
	}

	/**
	 * Handle search input
	 *
	 * @param {String} searchString - The input string
	 *
	 * @return {void}
	 */
	handleSearch( searchString ) {
		// Update the query state if needed.
		if ( this.query !== searchString ) {
			this.query = searchString;
		}

		// Check for an empty search string.
		if ( isEmpty( searchString ) ) {
			// Dispatch the filter change event with all templates.
			this._dispatchFilterChange( this.library.templates, 'all' );
			return;
		}

		// Initialize the fuse search instance.
		if ( ! this.fuse ) {
			this._initializeFuse();
		}

		// Perform the search and normalize the results.
		let results = this.fuse.search( searchString );
		const updatedResults = results.map( result => {
			return this.library.templates.find( template => template.id === result.item.id );
		} );

		// Dispatch the filter change event.
		this._dispatchFilterChange( updatedResults, 'search' );

		// Maybe send the search string to reporting.
		this._maybeReportSearch( searchString );
	}

	/**
	 * Dispatch a filter change event
	 *
	 * @param {Array} results - The filtered results
	 * @param {String} type - The filter type
	 *
	 * @return {void}
	 */
	_dispatchFilterChange( results, type ) {
		this.dispatchEvent(
			uapRtlCreateFilterEvent(
				type,
				this.query,
				results
			)
		);
	}

	/**
	 * Report the search to the server
	 *
	 * @param {String} searchString - The search string
	 *
	 * @return {void} - No return
	 */
	_maybeReportSearch( searchString ) {

		// Bail if the rest object is not available.
		if ( ! this.library.rest ) {
			return;
		}

		// Sanitize the search string.
		searchString = searchString.trim();

		// Remove any special characters.
		searchString = searchString.replace( /[^a-zA-Z0-9 ]/g, '' );

		// Confirm the search string is a minimum of 3 characters.
		if ( searchString.length < 3 ) {
			return;
		}

		// Lowercase the search string.
		searchString = searchString.toLowerCase();

		apiFetch( {
			url: `${ this.library.rest.url }`,
			method: 'POST',
			headers: {
				'X-WP-Nonce': UncannyAutomatorBackend.rest.nonce,
			  },
			data: {
				nonce: UncannyAutomatorBackend.rest.nonce,
				event: 'template-library-search',
				value: searchString,
			}
		} )
	}

	/**
	 * Render the search input
	 *
	 * @return {HTMLInputElement} - Rendered search input
	 */
	render() {

		// Check if the library is available
		const isReady = this.library && this.library.isReady;
		if ( ! isReady ) {
			return html``;
		}

		// Render the search input
		return html`
			<div id="uap-template-library__search-input-wrapper">
				<uo-icon id="magnifying-glass"></uo-icon>
				<input
					id="uap-template-library__search"
					type="search"
					.value=${this.query}
					@input=${( e ) => this._handleInput( e.target.value )}
					placeholder="${ escapeHTML( __( 'Search templates', 'uncanny-automator' ) ) }"
				/>
			</div>
		`;
	}

}